import { Config } from '@src/core/Config';
import { HALF_H, HALF_W } from '@src/game/arena/RoomLayout';

/**
 * Uniform grid over the room, linked-list per cell in typed arrays.
 * Rebuilt every tick for enemies; zero allocation after construction.
 */
export class SpatialHash {
  readonly cellSize: number;
  readonly cols: number;
  readonly rows: number;
  private readonly head: Int32Array;
  private readonly next: Int32Array;

  constructor(cellSize = 1.5) {
    this.cellSize = cellSize;
    this.cols = Math.ceil((HALF_W * 2 + 2) / cellSize);
    this.rows = Math.ceil((HALF_H * 2 + 2) / cellSize);
    this.head = new Int32Array(this.cols * this.rows).fill(-1);
    this.next = new Int32Array(Config.sim.maxEntities).fill(-1);
  }

  clear(): void {
    this.head.fill(-1);
  }

  cellX(x: number): number {
    const c = Math.floor((x + HALF_W + 1) / this.cellSize);
    return c < 0 ? 0 : c >= this.cols ? this.cols - 1 : c;
  }

  cellY(y: number): number {
    const r = Math.floor((y + HALF_H + 1) / this.cellSize);
    return r < 0 ? 0 : r >= this.rows ? this.rows - 1 : r;
  }

  insert(eid: number, x: number, y: number): void {
    const idx = this.cellY(y) * this.cols + this.cellX(x);
    this.next[eid] = this.head[idx];
    this.head[idx] = eid;
  }

  /**
   * Collect every entity whose cell intersects the circle (x, y, r) into `out`.
   * Returns the number written (capped at out.length). No allocation.
   */
  gather(x: number, y: number, r: number, out: Int32Array): number {
    const cx0 = this.cellX(x - r);
    const cx1 = this.cellX(x + r);
    const cy0 = this.cellY(y - r);
    const cy1 = this.cellY(y + r);
    let n = 0;
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        let eid = this.head[cy * this.cols + cx];
        while (eid !== -1) {
          if (n >= out.length) return n;
          out[n++] = eid;
          eid = this.next[eid];
        }
      }
    }
    return n;
  }
}
